import React, { useEffect, useState } from 'react'
import {
    Button,
    Grid,
    InputAdornment,
    makeStyles,
    TextField,
    Typography,
} from '@material-ui/core'
import {
    Search as SearchIcon,
    GetApp as DownloadIcon,
} from '@material-ui/icons'
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'

import { BackdropProgress } from '../../components'
import { send_command, useUserTableQuery } from 'components/api'

function userLabel(user) {
    return `${user.given_name || ''}|${user.family_name || ''}`.trim().toLowerCase()
}

const useStyles = makeStyles(theme => ({
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 16,
        flexWrap: 'wrap',
    },
    searchField: {
        flex: 1,
        minWidth: 200,
    },
    downloadBtn: {
        fontWeight: 700,
        color: '#fff',
        backgroundColor: '#4caf7d',
        whiteSpace: 'nowrap',
        '&:hover': {
            backgroundColor: '#3a9668',
        },
    },
    tableHead: {
        '& th': {
            fontWeight: 700,
            fontSize: '0.78rem',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: '#6b7585',
            borderBottom: '2px solid #d8dee6',
        },
    },
    tableRow: {
        '&:hover td': {
            backgroundColor: '#f8fafc',
        },
        '&:last-child td': {
            borderBottom: 0,
        },
    },
    badge: {
        display: 'inline-block',
        fontSize: '0.7rem',
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: 20,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
    },
    badgeActive: {
        backgroundColor: '#e0f5ef',
        color: '#1a7d54',
    },
    badgeAdmin: {
        backgroundColor: '#e8f0ff',
        color: '#3560c8',
    },
    footer: {
        fontSize: '0.8rem',
        color: '#9aa3b2',
        marginTop: 12,
    },
    empty: {
        textAlign: 'center',
        padding: '32px 0',
        color: '#6b7585',
    },
}))

export default function UserReport() {
    const classes = useStyles()
    const [isLoading, setIsLoading] = useState(false)
    const [users, setUsers] = useState([])
    const [error, setError] = useState(null)
    const [search, setSearch] = useState('')
    const { data: userData } = useUserTableQuery()

    const activeUserKeys = React.useMemo(() => {
        if (!Array.isArray(userData)) return new Set()
        return new Set(
            userData
                .filter(u => u.Enabled && u['custom:is_super_admin'] !== 'True')
                .map(u => userLabel(u))
        )
    }, [userData])

    const activeUsers = React.useMemo(() => (
        users.filter(u => activeUserKeys.has(userLabel(u)))
    ), [users, activeUserKeys])

    useEffect(() => {
        loadUsers()
    }, [])

    const loadUsers = () => {
        setIsLoading(true)
        setError(null)
        send_command('ADMIN_USER_REPORT')
            .then(data => {
                setUsers(Array.isArray(data) ? data : [])
            })
            .catch(() => {
                setError('Failed to load user report. Please try again.')
            })
            .finally(() => setIsLoading(false))
    }

    const filteredUsers = activeUsers.filter(user => {
        const q = search.toLowerCase()
        if (!q) return true
        const name = `${user.given_name || ''} ${user.family_name || ''}`.toLowerCase()
        const email = (user.email || '').toLowerCase()
        return name.includes(q) || email.includes(q)
    })

    const downloadCSV = () => {
        const header = 'First Name,Last Name,Email\n'
        const rows = filteredUsers
            .map(u => `"${u.given_name || ''}","${u.family_name || ''}","${u.email || ''}"`)
            .join('\n')
        const blob = new Blob([header + rows], { type: 'text/csv' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.download = `user-report-${new Date().toISOString().slice(0, 10)}.csv`
        a.href = url
        a.click()
        URL.revokeObjectURL(url)
    }

    if (isLoading) {
        return <BackdropProgress />
    }

    if (error) {
        return (
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Typography color="error">{error}</Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={loadUsers}
                        style={{ marginTop: 8 }}
                    >
                        Retry
                    </Button>
                </Grid>
            </Grid>
        )
    }

    return (
        <Grid container>
            <Grid item xs={12}>
                <div className={classes.toolbar}>
                    <TextField
                        className={classes.searchField}
                        variant="outlined"
                        size="small"
                        placeholder="Search by name or email…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" style={{ color: '#9aa3b2' }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="contained"
                        className={classes.downloadBtn}
                        startIcon={<DownloadIcon />}
                        onClick={downloadCSV}
                        disabled={filteredUsers.length === 0}
                        disableElevation
                    >
                        Download CSV
                    </Button>
                </div>
            </Grid>

            <Grid item xs={12}>
                <TableContainer component={Paper} elevation={0} style={{ border: '1px solid #eef0f4', borderRadius: 8 }}>
                    <Table size="small">
                        <TableHead className={classes.tableHead}>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredUsers.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={3} className={classes.empty}>
                                        {search ? 'No users match your search.' : 'No users found.'}
                                    </TableCell>
                                </TableRow>
                            )}
                            {filteredUsers.map((user, idx) => (
                                <TableRow key={user.email || idx} className={classes.tableRow}>
                                    <TableCell>
                                        {[user.given_name, user.family_name].filter(Boolean).join(' ') || '—'}
                                    </TableCell>
                                    <TableCell>{user.email || '—'}</TableCell>
                                    <TableCell>
                                        <span className={`${classes.badge} ${classes.badgeActive}`}>
                                            Active
                                        </span>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>

            <Grid item xs={12}>
                <Typography className={classes.footer}>
                    {search
                        ? `Showing ${filteredUsers.length} of ${activeUsers.length} users.`
                        : `${activeUsers.length} active user${activeUsers.length !== 1 ? 's' : ''} total.`
                    }
                </Typography>
            </Grid>
        </Grid>
    )
}
